import React, { useEffect } from 'react';
import { Col, Row, Button } from 'react-bootstrap';
import { Field, Form, getIn, withFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { BasicTooltip, FormikNumber, FormikSelect, Icon } from '../../../components';
import { getAnnualGratificationSettingsRequest } from '../../../requests/annualGratificationSettings';
import { camelCaseEmptyStringRecursive } from '../../../services/utils';
import './style.scss';

const AnnualGratificationSettingsForm = ({
  values,
  errors,
  touched,
  setFieldValue,
  setIsLoading,
  isSubmitting,
  yearOptions
}) => {
  const dispatch = useDispatch();
  const currentYearPeriod = getIn(values, 'annualGratificationSetting[yearPeriod]');
  const liquidProfit = getIn(values, 'annualGratificationSetting[liquidProfit]');
  const totalAnnualRemuneration = getIn(values, 'annualGratificationSetting[totalAnnualRemuneration]');

  const handleGetAnnualGratificationSetting = () => {
    if (!currentYearPeriod) return;
    setIsLoading(true);
    getAnnualGratificationSettingsRequest({
      dispatch,
      params: {
        year_period: values?.annualGratificationSetting?.yearPeriod
      },
      successCallback: ({ data }) => {
        const formatedData = camelCaseEmptyStringRecursive(data);
        if (formatedData?.id) {
          setFieldValue('annualGratificationSetting[id]', formatedData?.id);
        } else {
          setFieldValue('annualGratificationSetting[id]', null);
        }
        setFieldValue('annualGratificationSetting[liquidProfit]', formatedData?.liquidProfit || 0);
        setFieldValue(
          'annualGratificationSetting[totalAnnualRemuneration]',
          formatedData?.totalAnnualRemuneration || 0
        );
        setFieldValue('annualGratificationSetting[parsedLiquidProfit]', formatedData?.liquidProfit || 0);
        setFieldValue(
          'annualGratificationSetting[parsedTotalAnnualRemuneration]',
          formatedData?.totalAnnualRemuneration || 0
        );
        setFieldValue('annualGratificationSetting[factor]', formatedData?.factor || 0);
        setFieldValue('annualGratificationSetting[parsedFactor]', formatedData?.parsedFactor || 0);
        setFieldValue('annualGratificationSetting[annualBonus]', formatedData?.annualBonus || 0);
        setFieldValue('annualGratificationSetting[parsedAnnualBonus]', formatedData?.parsedAnnualBonus || 0);
        setIsLoading(false);
      },
      callback: () => {
        setIsLoading(false);
      }
    });
  };

  const handleFactorCalculation = () => {
    const factor = Number(parseFloat(liquidProfit) / parseFloat(totalAnnualRemuneration));
    setFieldValue('annualGratificationSetting[factor]', factor);
    setFieldValue('annualGratificationSetting[parsedFactor]', factor);
  };

  useEffect(handleGetAnnualGratificationSetting, [currentYearPeriod]);
  useEffect(handleFactorCalculation, [liquidProfit, totalAnnualRemuneration]);
  useEffect(() => {
    setFieldValue('annualGratificationSetting[yearPeriod]', new Date().getFullYear());
  }, [setFieldValue]);

  return (
    <>
      <Form>
        <Row className="align-items-center">
          <Col md={4}>
            <Row className="align-items-center mb-3">
              <Col xs={4}>
                <p className="mb-1 text-uppercase">Periodo a Pagar</p>
              </Col>
              <Col xs={6}>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <Field name="annualGratificationSetting[yearPeriod]">
                      {({ field }) => (
                        <FormikSelect
                          {...field}
                          fieldName={field.name}
                          label=""
                          placeholder="Seleccionar año"
                          defaultValue={currentYearPeriod}
                          options={yearOptions()}
                          onChange={data => setFieldValue(field.name, data ? data.value : '')}
                          className="w-100"
                        />
                      )}
                    </Field>
                  </div>
                  <span className="d-inline-flex align-items-center ml-4 question-mark">
                    <BasicTooltip text="Año para el que se realizará el cálculo de la gratificación">
                      <Icon icon="help-circle" width={18} />
                    </BasicTooltip>
                  </span>
                </div>
              </Col>
            </Row>
          </Col>

          <Col md={4}>
            <Row className="align-items-center mb-3">
              <Col xs={4}>
                <p className="mb-1 text-uppercase">Utilidad Líquida</p>
              </Col>
              <Col xs={6}>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <Field name="annualGratificationSetting[parsedLiquidProfit]">
                      {({ field }) => (
                        <FormikNumber
                          {...field}
                          fieldName="annualGratificationSetting[liquidProfit]"
                          label=""
                          leftAddon="$"
                          disabled={false}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                        />
                      )}
                    </Field>
                  </div>
                  <span className="d-inline-flex align-items-center ml-4 question-mark">
                    <BasicTooltip text="Utilidad después de los impuestos">
                      <Icon icon="help-circle" width={18} />
                    </BasicTooltip>
                  </span>
                </div>
              </Col>
            </Row>
          </Col>

          <Col md={4}>
            <Row className="align-items-center mb-3">
              <Col xs={4}>
                <p className="mb-1 text-uppercase">Remuneración Total Bruta</p>
              </Col>
              <Col xs={6}>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <Field name="annualGratificationSetting[parsedTotalAnnualRemuneration]">
                      {({ field }) => (
                        <FormikNumber
                          {...field}
                          fieldName="annualGratificationSetting[totalAnnualRemuneration]"
                          label=""
                          leftAddon="$"
                          disabled={false}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                        />
                      )}
                    </Field>
                  </div>
                  <span className="d-inline-flex align-items-center ml-4 question-mark">
                    <BasicTooltip text="Remuneración bruta anual">
                      <Icon icon="help-circle" width={18} />
                    </BasicTooltip>
                  </span>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className="align-items-center">
          <Col md={4}>
            <Row className="align-items-center mb-3">
              <Col xs={4}>
                <p className="mb-1 text-uppercase">Factor</p>
              </Col>
              <Col xs={6}>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <Field name="annualGratificationSetting[parsedFactor]">
                      {({ field }) => (
                        <FormikNumber
                          {...field}
                          fieldName="annualGratificationSetting[factor]"
                          decimalScale={4}
                          label=""
                          readOnly
                          errors={errors}
                          touched={touched}
                        />
                      )}
                    </Field>
                  </div>
                  <span className="d-inline-flex align-items-center ml-4 question-mark ">
                    <BasicTooltip text="Es el resultado de dividir : Utilidad Líquida / Rem total anual">
                      <Icon icon="help-circle" width={18} />
                    </BasicTooltip>
                  </span>
                </div>
              </Col>
            </Row>
          </Col>

          <Col md={4}>
            <Row className="align-items-center mb-3">
              <Col xs={4}>
                <p className="mb-1 text-uppercase">% Gratificación Anual</p>
              </Col>
              <Col xs={6}>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <Field name="annualGratificationSetting[parsedAnnualBonus]">
                      {({ field }) => (
                        <FormikNumber
                          {...field}
                          fieldName="annualGratificationSetting[annualBonus]"
                          label=""
                          leftAddon="%"
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                        />
                      )}
                    </Field>
                  </div>
                  <span className="d-inline-flex align-items-center ml-4 question-mark">
                    <BasicTooltip text="% legal que se debe aplicar para el cálculo">
                      <Icon icon="help-circle" width={18} />
                    </BasicTooltip>
                  </span>
                </div>
              </Col>
            </Row>
          </Col>

          <Col md={4}>
            <Row className="align-items-center">
              <Col xs={{ offset: 6, span: 4 }} className="d-flex justify-content-end">
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  Guardar
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </>
  );
};

const setInitialValues = () => {
  return {
    annualGratificationSetting: {
      id: null,
      yearPeriod: null,
      liquidProfit: 0,
      totalAnnualRemuneration: 0,
      factor: 0,
      annualBonus: 0
    }
  };
};

const validationSchema = Yup.object().shape({
  annualGratificationSetting: Yup.object().shape({
    yearPeriod: Yup.string().required('Periodo es requerido'),
    liquidProfit: Yup.number()
      .required('Utilidad Liquida es requerida')
      .positive('Utilidad debe ser un numero positivo'),
    totalAnnualRemuneration: Yup.number().required('Remuneración Total Anual es requerida'),
    factor: Yup.number()
      .required('Factor es requerido')
      .positive('Factor debe ser un numero positivo'),
    annualBonus: Yup.number()
      .required('Gratificación Anual es requerida')
      .max(100, 'Gratificación Anual no puede ser mayor a 100')
      .positive('Gratificación Anual debe ser un numero positivo')
  })
});

const handleSubmit = (values, { props, setSubmitting, setFieldValue }) => {
  const { formRequest } = props;
  formRequest(values, setSubmitting, setFieldValue);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: true
})(AnnualGratificationSettingsForm);
